/**
 * @file CaseRespondPage.jsx
 * @description Public, logged-out page behind the one-click buttons in §1
 *   expiry-notice emails: `abacases.com/case-respond?<signed params>`.
 *   Mirrors InterviewRespondPage.jsx's shape and reasoning closely — see that
 *   file for the parts only briefly restated here.
 *
 * ## This page has no authority
 * It renders what the server tells it and nothing more. The signature is
 * verified server-side (case-respond/index.ts) on every call:
 *   • GET  — preview: case label, status, days left, which action. No writes.
 *   • POST — performs the action.
 * A tampered or forged link renders the same "not valid" screen no matter
 * what the query string claims — this page never evaluates the signature
 * itself.
 *
 * ## The "filled" flow is two steps on purpose
 * The link only ever authorizes "may close this case." WHICH reason applies
 * — hired through ABAcases, or hired somewhere else — is a fact only the
 * agency can supply, and it doubles as the placement-rate metric, so it is
 * asked here as an explicit question rather than folded into the button in
 * the email. See case-respond/index.ts's file header for the full reasoning
 * (the reason travels as an UNSIGNED param; the signature never covers it).
 * Concretely: this page must NEVER close the case on the first tap of the
 * "mark filled" link. Forwarded emails get opened by whoever is covering the
 * inbox that day, and a single accidental tap must not be able to close a
 * case out from under an agency who never asked to.
 *
 * ## Logged out by design
 * Mounted from App.js ahead of every auth/onboarding/legal gate, so it
 * renders for someone with no session, on a phone, in an email client's
 * in-app browser. It never signs anyone in.
 *
 * The route is deliberately absent from src/data/seoRoutes.json: not
 * prerendered, not in the sitemap, and noindex'd at runtime (see the meta tag
 * below and the public/_headers X-Robots-Tag block for this path).
 *
 * @module components/CaseRespondPage
 */
import { useCallback, useEffect, useState } from "react";
import { T } from "../theme";
import { Btn } from "./UI";
import Logo from "./Logo";
import { CheckCircleIcon, AlertTriangleIcon, InfoIcon, ClockIcon, PauseIcon } from "./Icons";
import { usePageSeo } from "../lib/seo";
import { readCaseActionParams, callCaseRespond } from "../lib/caseRespond";

/**
 * Suppress the Referer for as long as this page is mounted, so the signed URL
 * in the address bar cannot leak to any host we talk to (see
 * InterviewRespondPage's identical helper).
 */
function useNoReferrer() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const el = document.createElement("meta");
    el.setAttribute("name", "referrer");
    el.setAttribute("content", "no-referrer");
    document.head.appendChild(el);
    return () => { try { document.head.removeChild(el); } catch { /* already gone */ } };
  }, []);
}

export default function CaseRespondPage() {
  usePageSeo({ title: "Respond to an opening | ABAcases", noindex: true });
  useNoReferrer();

  // Read once on mount, never re-derived — a refresh must still work.
  const [params] = useState(() =>
    (typeof window === "undefined" ? null : readCaseActionParams(window.location.search)));

  const [phase, setPhase] = useState(params ? "loading" : "preview"); // loading | preview | asking | submitting
  const [data,  setData]  = useState(params ? null : { ok: false, state: "invalid", reason: "missing" });
  // Only meaningful mid-`filled` flow: which attribution the agency picked,
  // before the POST that actually closes the case.
  const [reason, setReason] = useState(null);

  const load = useCallback(() => {
    if (!params) return;
    setPhase("loading");
    callCaseRespond("GET", params).then((res) => {
      setData(res);
      setPhase("preview");
    });
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const confirmNow = useCallback(() => {
    if (!params) return;
    setPhase("submitting");
    callCaseRespond("POST", params).then((res) => {
      setData(res);
      setPhase("preview");
    });
  }, [params]);

  // Step 1 of the filled flow: show the attribution question instead of
  // submitting anything yet. See the file header — this is the guard against
  // closing a case on the first click.
  const askFilled = useCallback(() => setPhase("asking"), []);

  const submitFilled = useCallback((pickedReason) => {
    if (!params) return;
    setReason(pickedReason);
    setPhase("submitting");
    callCaseRespond("POST", params, { reason: pickedReason }).then((res) => {
      setData(res);
      setPhase("preview");
    });
  }, [params]);

  const state  = data?.state || "loading";
  const action = data?.action || "";
  const c      = data?.case || {};
  const daysLeft = Number.isFinite(c.daysLeft) ? c.daysLeft : null;
  const dayWord  = daysLeft === 1 ? "1 day" : `${daysLeft} days`;

  // ── One screen description, derived from (phase, state, action). ───────────
  let s;
  if (phase === "loading") {
    s = { tone: "info", icon: ClockIcon, title: "Loading…" };
  } else if (phase === "asking") {
    // The attribution question — not yet a server response, so it is handled
    // before the state-driven branches below.
    s = {
      tone: "info", icon: InfoIcon,
      title: "Did you hire through ABAcases, or somewhere else?",
      body: c.label ? `For ${c.label}.` : "",
      askButtons: true,
    };
  } else if (state === "network" || state === "error") {
    s = {
      tone: "warn", icon: AlertTriangleIcon,
      title: state === "network" ? "Couldn't connect" : "Something went wrong",
      body:  state === "network"
        ? "Check your connection and try again."
        : "Please try again, or open ABAcases directly.",
      retry: true,
    };
  } else if (state === "expired") {
    s = {
      tone: "info", icon: ClockIcon,
      title: "This link has expired",
      body: "Open your dashboard to reconfirm or update this opening directly.",
      next: { href: "/dashboard", label: "Open my dashboard" },
    };
  } else if (state === "flagged") {
    s = {
      tone: "warn", icon: AlertTriangleIcon,
      title: "This opening needs admin review first",
      body: "It's been flagged for review and can't be changed from this link. Open your dashboard for details.",
      next: { href: "/dashboard", label: "Open my dashboard" },
    };
  } else if (state === "invalid") {
    const r = data?.reason;
    s = {
      tone: "warn", icon: AlertTriangleIcon,
      title:
        r === "not_found" ? "We couldn't find that opening"
        : r === "bad_reason" ? "Please pick one of the two options"
        : "This link isn't valid",
      body:
        r === "not_found" ? "It may have been removed. Open your dashboard to check your openings."
        : r === "bad_reason" ? "Try the link again from your email."
        : "Double-check you copied the whole link, or open your dashboard directly.",
      next: { href: "/dashboard", label: "Open my dashboard" },
    };
  } else if (state === "closed") {
    const alreadyLabel = String(data?.reason || "").replace("already_", "");
    s = {
      tone: "ok", icon: CheckCircleIcon,
      title: "This opening is already settled",
      body: `It's already ${alreadyLabel}. Nothing left to do here.`,
      next: { href: "/dashboard", label: "Open my dashboard" },
    };
  } else if (state === "reconfirmed_paused") {
    s = {
      tone: "ok", icon: PauseIcon,
      title: "Reconfirmed",
      body: "This opening is still paused. Open your dashboard to relist it.",
      next: { href: "/dashboard", label: "Open my dashboard" },
    };
  } else if (state === "done") {
    s = data?.result === "filled"
      ? {
          tone: "ok", icon: CheckCircleIcon,
          title: "Opening marked filled",
          body: reason === "filled_elsewhere"
            ? "Thanks for letting us know. Noted as hired outside ABAcases."
            : "Thanks for letting us know. Noted as hired through ABAcases.",
          next: { href: "/dashboard", label: "Open my dashboard" },
        }
      : {
          tone: "ok", icon: CheckCircleIcon,
          title: "Opening reconfirmed",
          body: "It stays visible to therapists on its usual schedule.",
          next: { href: "/dashboard", label: "Open my dashboard" },
        };
  } else if (state === "ready" && action === "confirm") {
    s = {
      tone: "ok", icon: CheckCircleIcon,
      title: "Reconfirm this opening",
      body: daysLeft != null
        ? `${c.label || "This opening"} ${c.status === "paused" ? "is paused" : `expires in ${dayWord}`}. Reconfirming keeps it visible to therapists. It's free.`
        : "Reconfirming keeps this opening visible to therapists. It's free.",
      button: "Reconfirm now", variant: "success", onPrimary: confirmNow,
    };
  } else if (state === "ready" && action === "filled") {
    s = {
      tone: "info", icon: InfoIcon,
      title: "Mark this opening filled",
      body: c.label ? `For ${c.label}. We'll ask one quick question first.` : "We'll ask one quick question first.",
      button: "Mark as filled", variant: "primary", onPrimary: askFilled,
    };
  } else {
    s = {
      tone: "warn", icon: AlertTriangleIcon,
      title: "This link isn't valid",
      body: "Double-check you copied the whole link, or open your dashboard directly.",
      next: { href: "/dashboard", label: "Open my dashboard" },
    };
  }

  const accent = s.tone === "warn" ? T.red : s.tone === "info" ? T.blue : T.green;
  const Icon = s.icon;
  const busy = phase === "loading" || phase === "submitting";

  return (
    <div style={{
      minHeight: "100vh", background: T.bg, fontFamily: T.fontBody, color: T.text,
      padding: "32px 16px calc(48px + env(safe-area-inset-bottom, 0px))",
      display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      <div style={{ marginBottom: 20 }}>
        <Logo variant="lockup" height={30} alt="ABAcases" />
      </div>

      <div style={{
        width: "100%", maxWidth: 460, background: T.surface,
        border: `1px solid ${T.border}`, borderRadius: T.radiusLg,
        boxShadow: T.shadow, padding: "26px 22px 24px", boxSizing: "border-box",
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: T.radiusFull, background: `${accent}14`,
          display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14,
        }}>
          <Icon size={20} color={accent} />
        </div>

        <h1 style={{
          margin: 0, fontSize: 20, lineHeight: 1.3, fontWeight: 800,
          letterSpacing: "-0.02em", color: T.text,
        }}>
          {s.title}
        </h1>

        {!s.askButtons && c.label && phase !== "loading" && (
          <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600, color: T.textLight }}>
            {c.label}
          </div>
        )}

        {s.body && (
          <p style={{ margin: "12px 0 0", fontSize: 14.5, lineHeight: 1.6, color: T.textMid }}>
            {s.body}
          </p>
        )}

        {s.askButtons && (
          <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 10 }}>
            <Btn
              variant="success"
              onClick={() => submitFilled("filled")}
              disabled={busy}
              style={{ width: "100%", padding: "13px 20px", fontSize: 15, borderRadius: T.radiusMd }}
            >
              {phase === "submitting" ? "Saving…" : "Hired through ABAcases"}
            </Btn>
            <Btn
              variant="outline"
              onClick={() => submitFilled("filled_elsewhere")}
              disabled={busy}
              style={{ width: "100%", padding: "13px 20px", fontSize: 15, borderRadius: T.radiusMd }}
            >
              {phase === "submitting" ? "Saving…" : "Hired somewhere else"}
            </Btn>
          </div>
        )}

        {s.button && !s.askButtons && (
          <div style={{ marginTop: 20 }}>
            <Btn
              variant={s.variant}
              onClick={s.onPrimary}
              disabled={busy}
              style={{ width: "100%", padding: "13px 20px", fontSize: 15.5, borderRadius: T.radiusMd }}
            >
              {phase === "submitting" ? "Saving…" : s.button}
            </Btn>
          </div>
        )}

        {s.retry && (
          <div style={{ marginTop: 20 }}>
            <Btn
              variant="outline"
              onClick={load}
              disabled={busy}
              style={{ width: "100%", padding: "13px 20px", fontSize: 15.5, borderRadius: T.radiusMd }}
            >
              Try again
            </Btn>
          </div>
        )}

        {s.next && (
          /* rel="noreferrer" so the signed URL never rides along as a Referer. */
          <a
            href={s.next.href}
            rel="noreferrer"
            style={{
              display: "inline-block", marginTop: 18, fontSize: 14,
              fontWeight: 700, color: T.blue, textDecoration: "none",
            }}
          >
            {s.next.label} →
          </a>
        )}
      </div>

      <div style={{ marginTop: 18, fontSize: 12, color: T.textLight, textAlign: "center", maxWidth: 460 }}>
        This link was sent to your agency's email on file and works without signing in.
      </div>
    </div>
  );
}
